import { formatDistanceToNow, type Locale } from "date-fns";

import { enUS, es, ptBR } from "date-fns/locale";

import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Card, CardContent } from "@/components/ui/card";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import EmptyState from "../../../shared/components/EmptyState";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import { useIsViewerOnly } from "../../auth/hooks/useHasRole";

import {
  useActiveUsersQuery,
  useRevokeUserAllTokens,
  useRevokeUserToken,
} from "../hooks/useActiveUsers";

import DesktopVersionCell from "./DesktopVersionCell";

import TableTooltipProvider from "./TableTooltipProvider";

const dateLocales: Record<string, Locale> = { es, en: enUS, "pt-BR": ptBR };

// The "all users" tab paints every account the backend knows about; cap the rows
// rendered at once so a flood of fake signups cannot freeze the page.
const ALL_USERS_RENDER_LIMIT = 300;

const ActiveUsersPage = () => {
  const { t, i18n } = useTranslation("common");

  const dateLoc = dateLocales[i18n.language] ?? es;

  const isViewerOnly = useIsViewerOnly();

  const { data, isLoading, isFetching } = useActiveUsersQuery();

  const revokeTokenMutation = useRevokeUserToken();

  const revokeAllForUserMutation = useRevokeUserAllTokens();

  // Filters for history tab

  const [userFilter, setUserFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "revoked"
  >("all");

  // Filter for the "all users" tab

  const [allFilter, setAllFilter] = useState("");

  const users = data?.data ?? [];

  const total = data?.meta.total ?? 0;

  const refreshedAt = data?.meta.refreshedAt;

  const isBusy = isLoading || isFetching;

  // Users with at least one active token

  const activeUsers = useMemo(() => {
    return users.filter((user) => user.tokens.some((token) => token.active));
  }, [users]);

  // Every user (the payload already carries all accounts, the active tab only

  // keeps the ones with a live token): latest desktop report first, then by name

  const allUsers = useMemo(() => {
    const seenAtMs = (value: string | null | undefined) => {
      const ms = value ? new Date(value).getTime() : Number.NaN;

      return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
    };

    // El reporte más nuevo ENTRE TODAS las instalaciones de la cuenta: quien usa
    // Eriol en la Mac hoy va arriba aunque su Windows esté parado hace meses.

    const ultimoReporte = (user: (typeof users)[number]) =>
      user.desktopInstalls.reduce(
        (masNuevo, install) => Math.max(masNuevo, seenAtMs(install.seenAt)),
        Number.NEGATIVE_INFINITY,
      );

    return [...users].sort((a, b) => {
      const aSeen = ultimoReporte(a);

      const bSeen = ultimoReporte(b);

      if (aSeen !== bSeen) {
        return aSeen > bSeen ? -1 : 1;
      }

      return a.name.localeCompare(b.name);
    });
  }, [users]);

  const filteredAllUsers = useMemo(() => {
    const needle = allFilter.trim().toLowerCase();

    if (!needle) {
      return allUsers;
    }

    return allUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(needle) ||
        user.email.toLowerCase().includes(needle),
    );
  }, [allUsers, allFilter]);

  const shownAllUsers = filteredAllUsers.slice(0, ALL_USERS_RENDER_LIMIT);

  // All tokens flattened for history view

  const allTokens = useMemo(() => {
    return users.flatMap((user) =>
      user.tokens.map((token) => ({
        ...token,

        userId: user.userId,

        userName: user.name,

        userEmail: user.email,

        userAccountType: user.accountType,
      })),
    );
  }, [users]);

  // Filtered tokens for history tab

  const filteredTokens = useMemo(() => {
    return allTokens.filter((token) => {
      const matchesUser =
        !userFilter ||
        token.userName.toLowerCase().includes(userFilter.toLowerCase()) ||
        token.userEmail.toLowerCase().includes(userFilter.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && token.active) ||
        (statusFilter === "revoked" && !token.active);

      return matchesUser && matchesStatus;
    });
  }, [allTokens, userFilter, statusFilter]);

  const headerSubtitle = useMemo(() => {
    if (!refreshedAt) {
      return t("USERS.subtitle");
    }

    return formatDistanceToNow(new Date(refreshedAt), {
      addSuffix: true,
      locale: dateLoc,
    });
  }, [refreshedAt, t, dateLoc]);

  const handleRevokeToken = async (tokenId: string) => {
    await revokeTokenMutation.mutateAsync(tokenId);
  };

  const handleRevokeUser = async (userId: string) => {
    await revokeAllForUserMutation.mutateAsync(userId);
  };

  const getAccountTypeBadge = (accountType: string | null) => {
    switch (accountType) {
      case "guest":
        return (
          <Badge className="bg-cyan-500/20 text-cyan-400">
            {t("USERS.accountTypeGuest")}
          </Badge>
        );

      case "organization":
        return (
          <Badge className="bg-amber-500/20 text-amber-400">
            {t("USERS.accountTypeOrg")}
          </Badge>
        );

      case "individual":
        return (
          <Badge className="bg-blue-500/20 text-blue-400">
            {t("USERS.accountTypeIndividual")}
          </Badge>
        );

      default:
        return (
          <span className="text-xs text-neutral-400">
            {accountType?.trim() || t("USERS.accountTypePending")}
          </span>
        );
    }
  };

  // Desde qué app se REGISTRÓ la cuenta (users.signupClientApp). No se mezcla con
  // getClientTypeBadge, que describe la sesión (la app que la cuenta usa hoy): un usuario que se
  // registró en la web y después instaló el escritorio tiene origen "web" y sesión "desktop".
  const getSignupOriginBadge = (origin: string | null | undefined) => {
    switch (origin) {
      case "web":
        return (
          <Badge className="bg-sky-500/20 text-sky-400">
            {t("USERS.originWeb")}
          </Badge>
        );

      case "guest":
        return (
          <Badge className="bg-amber-500/20 text-amber-400">
            {t("USERS.originGuest")}
          </Badge>
        );

      case "guest-pwa":
        return (
          <Badge className="bg-amber-500/20 text-amber-400">
            {t("USERS.originGuestPwa")}
          </Badge>
        );

      case "desktop":
        return (
          <Badge className="bg-violet-500/20 text-violet-400">
            {t("USERS.originDesktop")}
          </Badge>
        );

      case "mobile":
        return (
          <Badge className="bg-teal-500/20 text-teal-400">
            {t("USERS.originMobile")}
          </Badge>
        );

      case "mobile-pwa":
        return (
          <Badge className="bg-teal-500/20 text-teal-400">
            {t("USERS.originMobilePwa")}
          </Badge>
        );

      default:
        // Un valor que el backoffice no conoce (ClientApp nuevo) se muestra crudo en vez de
        // esconderse; sin dato va el guión, que es el caso de las cuentas viejas.
        return origin?.trim() ? (
          <span className="text-xs text-neutral-300">{origin}</span>
        ) : (
          <span className="text-xs text-neutral-500">—</span>
        );
    }
  };

  // Con qué método se registró. El backend lo GUARDA al crear la cuenta (users.signupMethod)
  // y lo sirve tal cual: no se deduce de authProvider ni de si hay contraseña, porque eso
  // describe cómo entra hoy —una cuenta sin contraseña gana una al usar "olvidé mi
  // contraseña"— y no cómo se dio de alta. Sin dato (cuentas viejas sin auditoría del alta)
  // viaja vacío y acá se ve "—".
  const getSignupMethodBadge = (method: string | null | undefined) => {
    switch (method) {
      case "google":
        return (
          <Badge className="bg-rose-500/20 text-rose-400">
            {t("USERS.methodGoogle")}
          </Badge>
        );

      case "email":
        return (
          <Badge className="bg-emerald-500/20 text-emerald-400">
            {t("USERS.methodEmail")}
          </Badge>
        );

      default:
        return method?.trim() ? (
          <span className="text-xs text-neutral-300">{method}</span>
        ) : (
          <span className="text-xs text-neutral-500">—</span>
        );
    }
  };

  const getClientTypeBadge = (clientType: string | null | undefined) => {
    switch (clientType) {
      case "desktop":
        return (
          <Badge className="bg-violet-500/20 text-violet-400">
            {t("USERS.clientDesktop")}
          </Badge>
        );

      case "mobile":
        return (
          <Badge className="bg-teal-500/20 text-teal-400">
            {t("USERS.clientMobile")}
          </Badge>
        );

      case "web":
        return (
          <Badge className="bg-sky-500/20 text-sky-400">
            {t("USERS.clientWeb")}
          </Badge>
        );

      default:
        return <span className="text-xs text-neutral-500">—</span>;
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("USERS.title")}
        subtitle={headerSubtitle}
        actions={
          <Badge
            variant="outline"
            className="border-neutral-700 text-neutral-400"
          >
            {t("USERS.totalActive")}:{" "}
            <span className="ml-1 font-semibold text-purple-400">{total}</span>
          </Badge>
        }
      />

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="active">{t("USERS.activeUsersTab")}</TabsTrigger>
          <TabsTrigger value="all">{t("USERS.allUsersTab")}</TabsTrigger>
          <TabsTrigger value="history">
            {t("USERS.tokenHistoryTab")}
          </TabsTrigger>
        </TabsList>

        {/* Tab: Active Users */}
        <TabsContent value="active">
          <Card className="border-neutral-800 bg-neutral-900/50">
            <TableTooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">
                      {t("USERS.user")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.type")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.signupOrigin")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.signupMethod")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.country")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.version")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.lastActivity")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.activeTokens")}
                    </TableHead>
                    {!isViewerOnly && (
                      <TableHead className="text-right text-neutral-400">
                        {t("USERS.actions")}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeUsers.map((user) => {
                    const activeTokenCount = user.tokens.filter(
                      (t) => t.active,
                    ).length;

                    return (
                      <TableRow
                        key={user.userId}
                        className="border-neutral-800 hover:bg-neutral-800/50"
                      >
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-neutral-100">
                              {user.name}
                            </span>
                            <span className="text-xs text-neutral-400">
                              {user.email}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getAccountTypeBadge(user.accountType)}
                        </TableCell>
                        <TableCell>
                          {getSignupOriginBadge(user.signupClientApp)}
                        </TableCell>
                        <TableCell>
                          {getSignupMethodBadge(user.signupMethod)}
                        </TableCell>
                        <TableCell className="text-sm text-neutral-300">
                          {user.country?.trim() || t("USERS.countryNotProvided")}
                        </TableCell>
                        <TableCell>
                          <DesktopVersionCell
                            installs={user.desktopInstalls}
                            dateLoc={dateLoc}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-neutral-300">
                          {user.lastActivityAt
                            ? formatDistanceToNow(new Date(user.lastActivityAt), {
                                addSuffix: true,

                                locale: dateLoc,
                              })
                            : t("USERS.noRecords")}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-500/20 text-green-400">
                            {activeTokenCount}{" "}
                            {activeTokenCount === 1
                              ? t("USERS.activeToken")
                              : t("USERS.activeTokens2")}
                          </Badge>
                        </TableCell>
                        {!isViewerOnly && (
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRevokeUser(user.userId)}
                              disabled={
                                activeTokenCount === 0 ||
                                revokeAllForUserMutation.isPending
                              }
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              {t("USERS.revokeAll")}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableTooltipProvider>

            {(isBusy || revokeAllForUserMutation.isPending) && (
              <LoadingOverlay />
            )}

            {!isBusy && activeUsers.length === 0 && (
              <EmptyState
                title={t("USERS.noActiveUsers")}
                description={t("USERS.noActiveUsersDesc")}
                className="m-6"
              />
            )}
          </Card>
        </TabsContent>

        {/* Tab: All Users */}
        <TabsContent value="all">
          <Card className="mb-4 border-neutral-800 bg-neutral-900/50">
            <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-end">
              <div className="flex flex-1 flex-col gap-2">
                <Label className="text-xs text-neutral-400">
                  {t("USERS.user")}
                </Label>
                <Input
                  type="search"
                  value={allFilter}
                  onChange={(e) => setAllFilter(e.target.value)}
                  placeholder={t("USERS.searchByNameOrEmail")}
                  disabled={isBusy}
                  className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
                />
              </div>
              <p className="text-xs text-neutral-400">
                {t("USERS.showingOf", {
                  shown: shownAllUsers.length,

                  total: filteredAllUsers.length,
                })}
              </p>
            </CardContent>
          </Card>

          <Card className="border-neutral-800 bg-neutral-900/50">
            <TableTooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">
                      {t("USERS.user")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.type")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.signupOrigin")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.signupMethod")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.country")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.version")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("USERS.lastActivity")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shownAllUsers.map((user) => {
                    const country =
                      user.country?.trim() || t("USERS.countryNotProvided");

                    const lastActivity = user.lastActivityAt
                      ? formatDistanceToNow(new Date(user.lastActivityAt), {
                          addSuffix: true,

                          locale: dateLoc,
                        })
                      : t("USERS.noRecords");

                    return (
                      <TableRow
                        key={user.userId}
                        className="border-neutral-800 hover:bg-neutral-800/50"
                      >
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-neutral-100">
                              {user.name}
                            </span>
                            <span className="text-xs text-neutral-400">
                              {user.email}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getAccountTypeBadge(user.accountType)}
                        </TableCell>
                        <TableCell>
                          {getSignupOriginBadge(user.signupClientApp)}
                        </TableCell>
                        <TableCell>
                          {getSignupMethodBadge(user.signupMethod)}
                        </TableCell>
                        <TableCell className="text-sm text-neutral-300">
                          {country}
                        </TableCell>
                        <TableCell>
                          <DesktopVersionCell
                            installs={user.desktopInstalls}
                            dateLoc={dateLoc}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-neutral-300">
                          {lastActivity}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableTooltipProvider>

            {isBusy && <LoadingOverlay />}

            {!isBusy && shownAllUsers.length === 0 && (
              <EmptyState title={t("USERS.noRecords")} className="m-6" />
            )}
          </Card>
        </TabsContent>

        {/* Tab: Token History */}
        <TabsContent value="history">
          {/* Filters */}
          <Card className="mb-4 border-neutral-800 bg-neutral-900/50">
            <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-end">
              <div className="flex flex-1 flex-col gap-2">
                <Label className="text-xs text-neutral-400">
                  {t("USERS.user")}
                </Label>
                <Input
                  type="search"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  placeholder={t("USERS.searchByNameOrEmail")}
                  disabled={isBusy}
                  className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-xs text-neutral-400">
                  {t("USERS.status")}
                </Label>
                <Select
                  value={statusFilter}
                  onValueChange={(v) =>
                    setStatusFilter(v as typeof statusFilter)
                  }
                  disabled={isBusy}
                >
                  <SelectTrigger className="w-[140px] border-neutral-700 bg-neutral-800/50 text-neutral-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-neutral-700 bg-neutral-900">
                    <SelectItem value="all" className="text-neutral-200">
                      {t("USERS.all")}
                    </SelectItem>
                    <SelectItem value="active" className="text-neutral-200">
                      {t("USERS.active")}
                    </SelectItem>
                    <SelectItem value="revoked" className="text-neutral-200">
                      {t("USERS.revoked")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Badge
                variant="outline"
                className="border-neutral-700 text-neutral-400"
              >
                {filteredTokens.length}{" "}
                {filteredTokens.length === 1
                  ? t("USERS.token")
                  : t("USERS.tokens")}
              </Badge>
            </CardContent>
          </Card>

          {/* Tokens Table */}
          <Card className="border-neutral-800 bg-neutral-900/50">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="text-neutral-400">
                    {t("USERS.user")}
                  </TableHead>
                  <TableHead className="text-neutral-400">
                    {t("USERS.tokenId")}
                  </TableHead>
                  <TableHead className="text-neutral-400">
                    {t("USERS.clientType")}
                  </TableHead>
                  <TableHead className="text-neutral-400">
                    {t("USERS.ipAddress")}
                  </TableHead>
                  <TableHead className="text-neutral-400">
                    {t("USERS.issued")}
                  </TableHead>
                  <TableHead className="text-neutral-400">
                    {t("USERS.status")}
                  </TableHead>
                  {!isViewerOnly && (
                    <TableHead className="text-right text-neutral-400">
                      {t("USERS.actions")}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTokens.map((token) => (
                  <TableRow
                    key={token.id}
                    className="border-neutral-800 hover:bg-neutral-800/50"
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-neutral-100">
                          {token.userName}
                        </span>
                        <span className="text-xs text-neutral-400">
                          {token.userEmail}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-neutral-800 px-2 py-1 font-mono text-xs text-neutral-300">
                        {token.id}
                      </code>
                    </TableCell>
                    <TableCell>
                      {token.userAgent ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">
                                {getClientTypeBadge(token.clientType)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs break-all border-neutral-700 bg-neutral-900 text-neutral-300">
                              {token.userAgent}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        getClientTypeBadge(token.clientType)
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-neutral-300">
                      {token.ipAddress || (
                        <span className="text-neutral-500">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-neutral-300">
                      {formatDistanceToNow(new Date(token.issuedAt), {
                        addSuffix: true,

                        locale: dateLoc,
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          token.active
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }
                      >
                        {token.active ? t("USERS.active") : t("USERS.revoked")}
                      </Badge>
                    </TableCell>
                    {!isViewerOnly && (
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevokeToken(token.id)}
                          disabled={
                            !token.active || revokeTokenMutation.isPending
                          }
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          {t("USERS.revoke")}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {(isBusy || revokeTokenMutation.isPending) && <LoadingOverlay />}

            {!isBusy && filteredTokens.length === 0 && (
              <EmptyState
                title={t("USERS.noTokens")}
                description={t("USERS.noTokensDesc")}
                className="m-6"
              />
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ActiveUsersPage;
